import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Phone, Video, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { VideoConfirmModal } from "./SupportVideo";

export function RoleChip({ label, color, className = "" }) {
  if (!label) return null;
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${className}`} style={{ background: `${color || "#64748B"}1A`, color: color || "#64748B" }} data-no-i18n>{label}</span>;
}

// Opens (or reuses) the private support chat; `call` → voice, `withVideo` → interactive video.
export async function openSupportChat(nav, agentId, call = false, withVideo = false) {
  try {
    const r = await api.post("/conversations/direct", { persona_id: agentId });
    nav(`/chat/${r.data.id}${call ? `?call=1${withVideo ? "&video=1" : ""}` : ""}`);
  } catch (e) { toast.error("Gagal membuka chat support"); }
}

// Interactive-video gate: paused / disabled → toast, else returns the config for the confirm modal.
export async function fetchVideoCfg(agentId) {
  try {
    const r = await api.get(`/support/video-config?agent_id=${encodeURIComponent(agentId)}`);
    if (r.data.paused) { toast.error("Video interaktif sedang dijeda sementara oleh platform — panggilan suara tetap tersedia"); return null; }
    if (!r.data.enabled) { toast.error("Video interaktif belum tersedia"); return null; }
    return r.data;
  } catch (e) { toast.error("Gagal memuat info video"); return null; }
}

function AgentCard({ a, onChat, onCall, onVideo }) {
  return (
    <article className="aivora-card aivora-card-hover flex min-w-[260px] snap-start flex-col p-4 sm:min-w-0" data-testid={`support-agent-card-${a.id}`} style={{ borderTop: `3px solid ${a.role_color || "#2F6BFF"}` }}>
      <div className="flex items-start gap-3">
        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-2xl shadow ring-2 ring-white">
          {a.portrait ? <img src={a.portrait} alt={a.name} className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center bg-[#EEF3FF] text-[#2F6BFF]"><LifeBuoy size={24} /></span>}
          <span className="absolute bottom-1 right-1 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-1.5 text-sm font-bold text-slate-900"><span data-no-i18n>{a.name}</span>{a.is_default && <span className="rounded-full bg-[#2F6BFF]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#2F6BFF]">Dukungan</span>}</p>
          <RoleChip label={a.role_label} color={a.role_color} className="mt-1" />
          <p className="mt-1.5 line-clamp-2 text-xs text-slate-500" data-no-i18n>{a.summary || "Siap membantu Anda"}</p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button onClick={onChat} className="btn-primary flex flex-1 items-center justify-center gap-2 py-2 text-xs" data-testid={`support-chat-${a.id}`}><MessageSquare size={14} /> Chat</button>
        <button onClick={onCall} title="Panggilan suara" className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#2F6BFF]/30 bg-white text-[#2F6BFF] hover:bg-[#EEF3FF]" data-testid={`support-call-${a.id}`}><Phone size={15} /></button>
        {a.video_avatar && <button onClick={onVideo} title="Panggilan video interaktif" className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#7C3AED]/30 bg-white text-[#7C3AED] hover:bg-[#F3EEFF]" data-testid={`support-video-${a.id}`}><Video size={15} /></button>}
      </div>
    </article>
  );
}

// Home "Bantuan" section: one card per enabled support agent (horizontal carousel on mobile, grid on desktop).
export function SupportAgentsSection() {
  const nav = useNavigate();
  const [agents, setAgents] = useState(null);
  const [videoCfg, setVideoCfg] = useState(null);
  useEffect(() => { api.get("/support/agents").then((r) => setAgents(r.data)).catch(() => setAgents([])); }, []);
  if (!agents || agents.length === 0) return null;
  const askVideo = async (a) => { const cfg = await fetchVideoCfg(a.id); if (cfg) setVideoCfg({ ...cfg, agent_id: a.id }); };
  return (
    <section className="aivora-card p-5" data-testid="home-support-agents" style={{ background: "linear-gradient(90deg, rgba(47,107,255,.06), rgba(124,58,237,.05))" }}>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-900 md:text-lg">Bantuan</h2>
          <p className="text-xs text-slate-500">Pilih support agent sesuai kebutuhan Anda — chat, panggilan suara, atau video interaktif.</p>
        </div>
        <span className="hidden rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-500 shadow-sm sm:inline" data-testid="support-agents-count">{agents.length} agent</span>
      </div>
      <div className="-mx-1 flex snap-x gap-3 overflow-x-auto px-1 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 xl:grid-cols-3">
        {agents.map((a) => <AgentCard key={a.id} a={a} onChat={() => openSupportChat(nav, a.id)} onCall={() => openSupportChat(nav, a.id, true)} onVideo={() => askVideo(a)} />)}
      </div>
      {videoCfg && <VideoConfirmModal cfg={videoCfg} onClose={() => setVideoCfg(null)} onConfirm={() => { const aid = videoCfg.agent_id; setVideoCfg(null); openSupportChat(nav, aid, true, true); }} />}
    </section>
  );
}
